import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

// ==================== UNITS ====================

export const createUnit = async (userId, unitData) => {
  try {
    const unitsRef = collection(db, 'units');
    const docRef = await addDoc(unitsRef, {
      ...unitData,
      userId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating unit:', error);
    return { success: false, error };
  }
};

export const getUnits = async (userId) => {
  try {
    const unitsRef = collection(db, 'units');
    const q = query(unitsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const units = [];
    querySnapshot.forEach((doc) => {
      units.push({ id: doc.id, ...doc.data() });
    });

    // Sort manually by unitNumber (ascending), then by createdAt (descending)
    units.sort((a, b) => {
      // First sort by unitNumber if both have it
      const aNum = a.unitNumber ? parseInt(a.unitNumber) : null;
      const bNum = b.unitNumber ? parseInt(b.unitNumber) : null;

      // If both have unit numbers, sort by unit number (ascending)
      if (aNum !== null && bNum !== null) {
        return aNum - bNum;
      }

      // If only one has a unit number, prioritize it
      if (aNum !== null) return -1;
      if (bNum !== null) return 1;

      // If neither has a unit number, sort by createdAt (newest first)
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, units };
  } catch (error) {
    console.error('Error getting units:', error);
    return { success: false, error };
  }
};

export const updateUnit = async (unitId, unitData) => {
  try {
    const unitRef = doc(db, 'units', unitId);
    await updateDoc(unitRef, {
      ...unitData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating unit:', error);
    return { success: false, error };
  }
};

export const deleteUnit = async (unitId) => {
  try {
    const unitRef = doc(db, 'units', unitId);
    await deleteDoc(unitRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting unit:', error);
    return { success: false, error };
  }
};

// ==================== LESSONS (Enhanced) ====================

export const createLesson = async (userId, lessonData) => {
  try {
    const lessonsRef = collection(db, 'lessons');
    const docRef = await addDoc(lessonsRef, {
      ...lessonData,
      userId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating lesson:', error);
    return { success: false, error };
  }
};

export const getLessons = async (userId) => {
  try {
    const lessonsRef = collection(db, 'lessons');
    const q = query(lessonsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const lessons = [];
    querySnapshot.forEach((doc) => {
      lessons.push({ id: doc.id, ...doc.data() });
    });

    // Sort manually by lessonNumber (ascending), then by createdAt (descending)
    lessons.sort((a, b) => {
      // First sort by lessonNumber if both have it
      const aNum = a.lessonNumber ? parseInt(a.lessonNumber) : null;
      const bNum = b.lessonNumber ? parseInt(b.lessonNumber) : null;

      // If both have lesson numbers, sort by lesson number (ascending)
      if (aNum !== null && bNum !== null) {
        return aNum - bNum;
      }

      // If only one has a lesson number, prioritize it
      if (aNum !== null) return -1;
      if (bNum !== null) return 1;

      // If neither has a lesson number, sort by createdAt (newest first)
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, lessons };
  } catch (error) {
    console.error('Error getting lessons:', error);
    return { success: false, error };
  }
};

export const updateLesson = async (lessonId, lessonData) => {
  try {
    const lessonRef = doc(db, 'lessons', lessonId);
    await updateDoc(lessonRef, {
      ...lessonData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating lesson:', error);
    return { success: false, error };
  }
};

export const deleteLesson = async (lessonId) => {
  try {
    const lessonRef = doc(db, 'lessons', lessonId);
    await deleteDoc(lessonRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting lesson:', error);
    return { success: false, error };
  }
};

// ==================== CLASSES ====================

export const createClass = async (userId, classData) => {
  try {
    const classesRef = collection(db, 'classes');
    const docRef = await addDoc(classesRef, {
      ...classData,
      userId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating class:', error);
    return { success: false, error };
  }
};

export const getClasses = async (userId) => {
  try {
    console.log('Fetching classes for userId:', userId);
    const classesRef = collection(db, 'classes');

    // Try without orderBy first to avoid index issues
    const q = query(classesRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const classes = [];
    querySnapshot.forEach((doc) => {
      classes.push({ id: doc.id, ...doc.data() });
    });

    // Sort manually by createdAt
    classes.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    console.log('Classes fetched successfully:', classes.length);
    return { success: true, classes };
  } catch (error) {
    console.error('Error getting classes:', error);
    console.error('Error details:', error.message, error.code);
    return { success: false, error };
  }
};

export const updateClass = async (classId, classData) => {
  try {
    const classRef = doc(db, 'classes', classId);
    await updateDoc(classRef, {
      ...classData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating class:', error);
    return { success: false, error };
  }
};

export const deleteClass = async (classId) => {
  try {
    const classRef = doc(db, 'classes', classId);
    await deleteDoc(classRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting class:', error);
    return { success: false, error };
  }
};

// ==================== CLASSROOMS ====================

export const createClassroom = async (userId, classroomData) => {
  try {
    const classroomsRef = collection(db, 'classrooms');
    const docRef = await addDoc(classroomsRef, {
      ...classroomData,
      userId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating classroom:', error);
    return { success: false, error };
  }
};

export const getClassrooms = async (userId) => {
  try {
    const classroomsRef = collection(db, 'classrooms');
    const q = query(classroomsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const classrooms = [];
    querySnapshot.forEach((doc) => {
      classrooms.push({ id: doc.id, ...doc.data() });
    });

    // Sort manually by createdAt
    classrooms.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, classrooms };
  } catch (error) {
    console.error('Error getting classrooms:', error);
    return { success: false, error };
  }
};

export const updateClassroom = async (classroomId, classroomData) => {
  try {
    const classroomRef = doc(db, 'classrooms', classroomId);
    await updateDoc(classroomRef, {
      ...classroomData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating classroom:', error);
    return { success: false, error };
  }
};

export const deleteClassroom = async (classroomId) => {
  try {
    const classroomRef = doc(db, 'classrooms', classroomId);
    await deleteDoc(classroomRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting classroom:', error);
    return { success: false, error };
  }
};

// ==================== SCHEDULES ====================

export const createSchedule = async (userId, scheduleData) => {
  try {
    const schedulesRef = collection(db, 'schedules');
    const docRef = await addDoc(schedulesRef, {
      ...scheduleData,
      userId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating schedule:', error);
    return { success: false, error };
  }
};

export const getSchedules = async (userId) => {
  try {
    const schedulesRef = collection(db, 'schedules');
    const q = query(schedulesRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const schedules = [];
    querySnapshot.forEach((doc) => {
      schedules.push({ id: doc.id, ...doc.data() });
    });

    // Sort manually by dayOfWeek and startTime
    schedules.sort((a, b) => {
      if (a.dayOfWeek !== b.dayOfWeek) {
        return (a.dayOfWeek || 0) - (b.dayOfWeek || 0);
      }
      return (a.startTime || '').localeCompare(b.startTime || '');
    });

    return { success: true, schedules };
  } catch (error) {
    console.error('Error getting schedules:', error);
    return { success: false, error };
  }
};

export const updateSchedule = async (scheduleId, scheduleData) => {
  try {
    const scheduleRef = doc(db, 'schedules', scheduleId);
    await updateDoc(scheduleRef, {
      ...scheduleData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating schedule:', error);
    return { success: false, error };
  }
};

export const deleteSchedule = async (scheduleId) => {
  try {
    const scheduleRef = doc(db, 'schedules', scheduleId);
    await deleteDoc(scheduleRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting schedule:', error);
    return { success: false, error };
  }
};

// ==================== SCOPE & SEQUENCE ====================

export const createScopeAndSequence = async (userId, classId, scopeData) => {
  try {
    const scopeRef = collection(db, 'scopeAndSequence');
    const docRef = await addDoc(scopeRef, {
      ...scopeData,
      userId,
      classId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating scope and sequence:', error);
    return { success: false, error };
  }
};

export const getScopeAndSequenceByClass = async (classId) => {
  try {
    const scopeRef = collection(db, 'scopeAndSequence');
    const q = query(scopeRef, where('classId', '==', classId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { success: true, scopeAndSequence: null };
    }

    const doc = querySnapshot.docs[0];
    return {
      success: true,
      scopeAndSequence: { id: doc.id, ...doc.data() }
    };
  } catch (error) {
    console.error('Error getting scope and sequence:', error);
    return { success: false, error };
  }
};

export const updateScopeAndSequence = async (scopeId, scopeData) => {
  try {
    const scopeRef = doc(db, 'scopeAndSequence', scopeId);
    await updateDoc(scopeRef, {
      ...scopeData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating scope and sequence:', error);
    return { success: false, error };
  }
};

export const deleteScopeAndSequence = async (scopeId) => {
  try {
    const scopeRef = doc(db, 'scopeAndSequence', scopeId);
    await deleteDoc(scopeRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting scope and sequence:', error);
    return { success: false, error };
  }
};
